import moment, { Moment } from "moment";
import {
  DisableDurationPreset,
  IServiceDisableMetadata,
} from "../../../../../../types/audit";
import { IScheduledService } from "../../../../../../types/shared";
import { computeScheduledUntil } from "../../../../../../helpers/audit/utils";
import i18n from "../../../../../../i18n/i18n";

export interface ActionModalReasonOption {
  key: string;
  label: string;
}

export const DURATION_PRESETS: DisableDurationPreset[] = [
  "1h",
  "2h",
  "1d",
  "1w",
  "1m",
  "custom",
  "permanent",
];

const PRESET_DURATIONS = new Set<DisableDurationPreset>([
  "1h",
  "2h",
  "1d",
  "1w",
  "1m",
  "permanent",
]);

const getDateLocale = () => (i18n.language?.includes("ar") ? "ar" : "en");

export const formatCustomDate = (date: Moment) =>
  date.clone().locale(getDateLocale()).format("DD MMM YYYY, hh:mm A");

export const getDurationDisplayLabel = (
  duration: DisableDurationPreset,
  customDate?: Moment | null
): string => {
  if (duration === "custom" && customDate) {
    return formatCustomDate(customDate);
  }
  return i18n.t(`serviceDisableModal:duration_${duration}`);
};

export const getAutoResumeHint = ({
  serviceName,
  duration,
  customDate,
  fallbackHint,
}: {
  serviceName?: string;
  duration: DisableDurationPreset;
  customDate?: Moment | null;
  fallbackHint?: string;
}): string | undefined => {
  if (duration === "custom" && customDate && serviceName) {
    return i18n.t("serviceDisableModal:autoResumeOnDateLabel", {
      service: serviceName,
      date: formatCustomDate(customDate),
    });
  }
  return fallbackHint;
};

export const resolveInitialDuration = (
  duration?: DisableDurationPreset | null,
  scheduledUntil?: number | null
): DisableDurationPreset => {
  if (duration === "custom") {
    return "custom";
  }
  if (duration && PRESET_DURATIONS.has(duration)) {
    return duration;
  }
  if (scheduledUntil) {
    return "custom";
  }
  return duration || "permanent";
};

export const resolveReasonKey = (
  reason: string | undefined,
  options: ActionModalReasonOption[]
): { reasonKey: string; customReasonText: string } => {
  if (!reason?.trim()) {
    return { reasonKey: "", customReasonText: "" };
  }

  const match = options.find(
    (option) => option.key !== "other" && option.label === reason
  );

  if (match) {
    return { reasonKey: match.key, customReasonText: "" };
  }

  return { reasonKey: "other", customReasonText: reason };
};

export interface PreviousSchedule {
  duration: DisableDurationPreset;
  customDate?: Moment | null;
  scheduledUntil: number | null;
}

/**
 * True when `duration`/`customDate` match the schedule the modal was opened
 * with. Preset durations (1h/2h/1d/1w/1m) are relative to "now", so
 * re-confirming an untouched preset must not recompute a fresh end date —
 * only an actual change to the selection should move scheduled_at.
 */
const isSameSchedule = (
  duration: DisableDurationPreset,
  customDate: Moment | null | undefined,
  previous: PreviousSchedule
): boolean => {
  if (duration !== previous.duration) {
    return false;
  }
  if (duration !== "custom") {
    return true;
  }
  return (customDate?.valueOf() ?? null) === (previous.customDate?.valueOf() ?? null);
};

export const buildDisableMetadata = (
  reason: string,
  duration: DisableDurationPreset,
  customDate?: Moment | null,
  previous?: PreviousSchedule | null
): IServiceDisableMetadata => {
  const keepPreviousSchedule =
    !!previous &&
    previous.scheduledUntil !== null &&
    isSameSchedule(duration, customDate, previous);

  const { scheduledUntil, isPermanent, isScheduled } = keepPreviousSchedule
    ? { scheduledUntil: previous!.scheduledUntil, isPermanent: false, isScheduled: true }
    : computeScheduledUntil(duration, customDate);

  // The end date is the schedule: a disable is revertible exactly when it
  // has a future end date (`scheduled_at` on the wire).
  const is_revertible =
    !isPermanent && scheduledUntil !== null && scheduledUntil > moment().valueOf();

  return {
    reason,
    duration,
    scheduledUntil,
    isPermanent,
    isScheduled,
    is_revertible,
  };
};

export const validateActionModalForm = ({
  reasonRequired,
  reasonKey,
  customReasonText,
  showDuration,
  durationKey,
  customDate,
  reasonRequiredError,
  customDateRequiredError = "Please select a date and time",
  minDate,
  minDateError,
}: {
  reasonRequired: boolean;
  reasonKey: string;
  customReasonText: string;
  showDuration: boolean;
  durationKey: DisableDurationPreset;
  customDate: Moment | null;
  reasonRequiredError: string;
  customDateRequiredError?: string;
  minDate?: Moment | null;
  minDateError?: string;
}): string | null => {
  if (reasonRequired && !reasonKey) {
    return reasonRequiredError;
  }

  if (reasonKey === "other" && !customReasonText.trim()) {
    return reasonRequiredError;
  }

  if (showDuration && durationKey === "custom" && !customDate) {
    return customDateRequiredError;
  }

  if (
    showDuration &&
    durationKey === "custom" &&
    customDate &&
    !customDate.isAfter(moment())
  ) {
    return customDateRequiredError;
  }

  if (
    showDuration &&
    durationKey === "custom" &&
    customDate &&
    minDate &&
    customDate.isBefore(minDate)
  ) {
    return minDateError || customDateRequiredError;
  }

  return null;
};

export const resolveDisableConfirmBehavior = (
  metadata: IServiceDisableMetadata
) => ({
  shouldSetScheduled: metadata.isScheduled,
  shouldRemoveScheduled: !metadata.isScheduled,
  shouldApplyToggleOff: true,
});

export const scheduledUntilToMoment = (
  scheduledUntil: number | null | undefined
): Moment | null => {
  if (!scheduledUntil) {
    return null;
  }

  return moment(scheduledUntil);
};

/**
 * Restores disable metadata from an API `scheduled_services` entry
 * ({ reason, is_revertible, scheduled_at }), so schedules survive a page
 * refresh. `scheduled_at` is the absolute end date of the disable.
 */
export const scheduledServiceToMetadata = (
  service: IScheduledService
): IServiceDisableMetadata => {
  const scheduledUntil = scheduledAtToMs(service.scheduled_at);
  const isPermanent = !service.is_revertible && scheduledUntil === null;

  return {
    reason: service.reason || "",
    duration: scheduledUntil !== null ? "custom" : "permanent",
    scheduledUntil,
    isPermanent,
    isScheduled: !isPermanent,
    is_revertible: service.is_revertible === true,
  };
};

/** ISO string / epoch seconds / epoch ms -> epoch ms, or null. */
export const scheduledAtToMs = (
  value: string | number | null | undefined
): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed =
    typeof value === "number"
      ? moment(value < 1e12 ? value * 1000 : value)
      : moment(value);
  return parsed.isValid() ? parsed.valueOf() : null;
};

export const resolveScheduledUntilMs = (
  metadata: IServiceDisableMetadata | undefined,
  apiScheduledAt: string | number | null | undefined
): number | null => {
  if (metadata?.scheduledUntil != null) return metadata.scheduledUntil;
  return scheduledAtToMs(apiScheduledAt);
};

/**
 * Drive-thru may resume at the same time as pickup or later.
 * An earlier Drive-thru resume is invalid; permanent (no date) is later.
 */
export const isDriveThruScheduleBeforePickup = (
  driveThruScheduledUntil: number | null | undefined,
  pickupScheduledUntil: number | null | undefined
): boolean => {
  if (pickupScheduledUntil == null || pickupScheduledUntil <= 0) return false;
  if (driveThruScheduledUntil == null) return false;
  return driveThruScheduledUntil < pickupScheduledUntil;
};

export const clampMetadataToMinScheduledUntil = (
  metadata: IServiceDisableMetadata,
  minScheduledUntil: number | null | undefined
): IServiceDisableMetadata => {
  if (!isDriveThruScheduleBeforePickup(metadata.scheduledUntil, minScheduledUntil)) {
    return metadata;
  }

  const scheduledUntil = minScheduledUntil as number;
  return {
    ...metadata,
    duration: "custom",
    scheduledUntil,
    isPermanent: false,
    isScheduled: true,
    is_revertible: scheduledUntil > moment().valueOf(),
  };
};

export const isDurationBeforeMinDate = (
  duration: DisableDurationPreset,
  minDateMs: number | null | undefined
): boolean => {
  if (!minDateMs || duration === "permanent" || duration === "custom") {
    return false;
  }
  const { scheduledUntil } = computeScheduledUntil(duration);
  return scheduledUntil != null && scheduledUntil < minDateMs;
};
